import React, { useEffect, useRef } from 'react'
import Globe, { GlobeInstance } from 'globe.gl'
import { Box } from '@mui/material'

interface MarkerData {
  lat: number
  lng: number
  size: number
  name: string
}

const markerSvg = `<div style="
  border-radius: 50%;
  width: 10px;
  height: 10px;
  background: linear-gradient(270deg, #00DA33, #1B1B1B);
"></div>`

const countries = [
  { name: 'USA', lat: 38.0, lng: -97.0 },
  { name: 'Canada', lat: 56.1304, lng: -106.3468 },
  { name: 'Brazil', lat: -14.235, lng: -51.9253 },
  { name: 'UK', lat: 55.3781, lng: -3.436 },
  { name: 'India', lat: 20.5937, lng: 78.9629 },
  { name: 'Australia', lat: -25.2744, lng: 133.7751 },
  { name: 'Germany', lat: 51.1657, lng: 10.4515 },
  { name: 'Japan', lat: 36.2048, lng: 138.2529 },
  { name: 'South Africa', lat: -30.5595, lng: 22.9375 },
  { name: 'Russia', lat: 61.524, lng: 105.3188 },
  { name: 'Israel', lat: 31.0461, lng: 34.8516 },
]

// Генерация случайных пар (start ≠ end)
function getRandomArcs(count: number) {
  const arcs = []
  for (let i = 0; i < count; i++) {
    const startIdx = Math.floor(Math.random() * countries.length)
    let endIdx = Math.floor(Math.random() * countries.length)
    // Гарантируем, что start ≠ end
    while (endIdx === startIdx) {
      endIdx = Math.floor(Math.random() * countries.length)
    }
    arcs.push({
      startLat: countries[startIdx].lat,
      startLng: countries[startIdx].lng,
      endLat: countries[endIdx].lat,
      endLng: countries[endIdx].lng,
      color: '#00DA33',
    })
  }
  return arcs
}

const GlobeComponent: React.FC<{ globeImageUrl?: string }> = ({
  globeImageUrl,
}) => {
  const globeRef = useRef<HTMLDivElement>(null)
  const globeInstanceRef = useRef<GlobeInstance | null>(null)

  // Маркеры
  const gData: MarkerData[] = countries.map(c => ({
    name: c.name,
    lat: c.lat,
    lng: c.lng,
    size: 14,
  }))

  // Линии между случайными странами
  const arcsData = getRandomArcs(10) // 10 случайных линий

  const updateGlobeSize = () => {
    if (globeRef.current && globeInstanceRef.current) {
      const width = globeRef.current.offsetWidth
      const height = globeRef.current.offsetHeight
      globeInstanceRef.current.width(width)
      globeInstanceRef.current.height(height)
    }
  }

  useEffect(() => {
    if (!globeRef.current) return

    if (!globeInstanceRef.current) {
      globeInstanceRef.current = new Globe(globeRef.current)
        .globeImageUrl(globeImageUrl || '/world_map_clear.jpg')
        .backgroundColor('rgba(0, 0, 0, 0)')
        .showAtmosphere(false)
        // Маркеры
        .htmlElementsData(gData)
        .htmlElement(datum => {
          const data = datum as MarkerData
          const container = document.createElement('div')
          container.style.position = 'relative'
          container.style.width = '0'
          container.style.height = '0'

          const dot = document.createElement('div')
          dot.innerHTML = markerSvg
          Object.assign(dot.style, {
            position: 'absolute',
            left: '0',
            top: '0',
            transform: 'translate(-50%, -50%)',
            zIndex: '2',
            width: `${data.size}px`,
            pointerEvents: 'auto',
            cursor: 'pointer',
          })

          container.append(dot)
          return container
        })
        // Линии
        .arcsData(arcsData)
        .arcColor('color')
        .arcStroke(1.5)
        .arcAltitude(0.2)
        .arcDashLength(0.4)
        .arcDashGap(0.2)
        .arcDashInitialGap(() => Math.random())
        .arcDashAnimateTime(2000)

      globeInstanceRef.current.pointOfView({ lat: 0, lng: 0, altitude: 3 }, 0)
      globeInstanceRef.current.controls().autoRotate = true
      globeInstanceRef.current.controls().autoRotateSpeed = 0.55

      const controls = globeInstanceRef.current.controls()
      controls.enableZoom = false
      controls.zoomSpeed = 0
      controls.enablePan = false
    }
    updateGlobeSize()
    window.addEventListener('resize', updateGlobeSize)
    return () => window.removeEventListener('resize', updateGlobeSize)
    // eslint-disable-next-line
  }, [globeImageUrl])

  return (
    <Box
      component='div'
      ref={globeRef}
      sx={{
        width: '100%',
        aspectRatio: '1',
        maxWidth: { xs: '600px', md: '800px' },
        margin: '0 auto',
        height: { xs: '450px', sm: '600px', md: 'auto' },
        minHeight: '300px',
        position: 'absolute',
        top: { xs: '-150px', md: '-100px', lg: '-120px', xl: '-180px' },
        left: { xs: '50%', md: '0' },
        transform: { xs: 'translateX(-50%)', md: 'translateX(0)' },
      }}
    />
  )
}

export default GlobeComponent
